/**
 * The PYP student (§2.4): Home, My classes, Portfolio (Projects is the Grade 5 Exhibition, reached
 * from the nav). No simulator, no bank, no e-assessment, and no bands or percentages anywhere.
 * The portfolio view is shared with the family door, which only ever receives shared pieces.
 */
import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useAction, useQuery } from '../../lib/useQuery';
import { Btn, DesignShell, Empty, ErrorNote, Loading, Modal, S } from '../../design/kit';
import { BreadthCounts, ElementChips, ExhibitionCard, PieceCard, kindLabel, type Evidence, type PortfolioView } from './ui';

export interface PypMe extends PortfolioView {
  pyp: true;
  classes: { id: string; name: string; grade: string; teachers: string[]; evidence: number }[];
  recent: Evidence[];
}

function useMe() {
  return useQuery<PypMe | { pyp: false }>('/pyp/me');
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';
};

export function PypStudentHome() {
  const { user, school } = useSession();
  const q = useMe();
  const d = q.data && q.data.pyp ? q.data : null;
  return (
    <DesignShell brand={school?.name} title={`${greeting()}, ${user?.name?.split(' ')[0] ?? 'there'}`} footer={{ name: user?.name ?? '', sub: d?.classes.map((c) => c.name).join(', ') }} actions={<Btn kind="secondary" to="/join">Join with a code</Btn>}>
      {q.error ? <ErrorNote error={q.error} retry={q.reload} /> : null}
      {!q.data ? (q.error ? null : <Loading />) : !d ? <Empty title="Nothing here yet" /> : (
        <div className="dashgrid">
          <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
            <section aria-label="Your portfolio" style={{ ...S.card, display: 'grid', gap: '6px' }}>
              <div style={S.between}>
                <h2 style={{ ...S.h3, margin: 0 }}>Your portfolio</h2>
                <Btn kind="secondarySm" to="/student/portfolio">Open</Btn>
              </div>
              <span style={S.small}>{d.pieces.length} piece{d.pieces.length === 1 ? '' : 's'} chosen so far{d.available.length ? ` · ${d.available.length} more you could add` : ''}.</span>
            </section>
            <section aria-label="Your latest work" style={{ ...S.card, display: 'grid', gap: '10px' }}>
              <h2 style={{ ...S.h3, margin: 0 }}>Your latest work</h2>
              {d.recent.length === 0 ? <span style={S.mutedXs}>Your teacher adds photos, recordings and writing here as you work.</span> : d.recent.map((e) => (
                <div key={e.id} style={{ display: 'grid', gap: '4px' }}>
                  <span style={{ ...S.small, fontWeight: 600 }}>{e.title}</span>
                  <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={S.mutedXs}>{kindLabel(e)} · {new Date(e.at).toLocaleDateString()}</span>
                    <ElementChips elements={e.elements} />
                  </span>
                </div>
              ))}
            </section>
          </div>
          <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
            {d.exhibition ? <ExhibitionCard exhibition={d.exhibition} to={d.exhibition.projectId ? `/student/projects/${d.exhibition.projectId}` : '/student/projects'} /> : null}
            <section aria-label="Your classes" style={{ ...S.card, display: 'grid', gap: '6px' }}>
              <h2 style={{ ...S.h3, margin: 0 }}>Your classes</h2>
              {d.classes.map((c) => <span key={c.id} style={S.small}>{c.name}{c.teachers.length ? ` · ${c.teachers.join(', ')}` : ''}</span>)}
            </section>
          </div>
        </div>
      )}
    </DesignShell>
  );
}

export function PypStudentClasses() {
  const { user, school } = useSession();
  const q = useMe();
  const d = q.data && q.data.pyp ? q.data : null;
  return (
    <DesignShell brand={school?.name} title="My classes" footer={{ name: user?.name ?? '' }}>
      {q.error ? <ErrorNote error={q.error} retry={q.reload} /> : null}
      {!q.data ? (q.error ? null : <Loading />) : !d ? <Empty title="No classes yet" /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
          {d.classes.map((c) => (
            <section key={c.id} aria-label={c.name} style={S.card}>
              <h2 style={{ ...S.h3, margin: 0 }}>{c.name}</h2>
              <div style={S.sub}>{[c.grade, c.teachers.join(', ')].filter(Boolean).join(' · ')}</div>
              <div style={{ ...S.mutedXs, marginTop: '12px' }}>{c.evidence ? `${c.evidence} piece${c.evidence === 1 ? '' : 's'} of your work collected this term` : 'Nothing collected yet'}</div>
            </section>
          ))}
        </div>
      )}
    </DesignShell>
  );
}

/** The portfolio for the student (curate) and for the family (shared pieces only, read only). */
export function PypPortfolioBody({ view, onAdd }: { view: PortfolioView; onAdd?: () => void }) {
  return (
    <div className="dashgrid">
      <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
        {view.pieces.length === 0 ? (
          <div style={S.card}><Empty title="No pieces yet" body={onAdd ? 'Choose a piece of your work and say why it matters to you.' : 'Pieces appear here once the teacher shares them with you.'} action={onAdd && view.available.length ? <Btn kind="primarySm" onClick={onAdd}>Add a piece</Btn> : undefined} /></div>
        ) : view.pieces.map((p) => <PieceCard key={p.id} piece={p} />)}
      </div>
      <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
        <BreadthCounts counts={view.breadth} thin={view.thin} name={view.student.name} />
        {view.exhibition ? <ExhibitionCard exhibition={view.exhibition} /> : null}
      </div>
    </div>
  );
}

export function PypStudentPortfolio() {
  const { user, school } = useSession();
  const q = useMe();
  const d = q.data && q.data.pyp ? q.data : null;
  const [adding, setAdding] = useState(false);
  return (
    <DesignShell
      brand={school?.name}
      title="Your portfolio"
      footer={{ name: user?.name ?? '' }}
      meta={<span>Chosen by you and your teacher. Each piece has a few words on why it matters — never a score.</span>}
      actions={d && d.available.length ? <Btn kind="primarySm" onClick={() => setAdding(true)}>Add a piece</Btn> : undefined}
    >
      {q.error ? <ErrorNote error={q.error} retry={q.reload} /> : null}
      {!q.data ? (q.error ? null : <Loading />) : !d ? <Empty title="Nothing here yet" /> : <PypPortfolioBody view={d} onAdd={() => setAdding(true)} />}
      {adding && d ? <ChoosePiece available={d.available} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); q.reload(); }} /> : null}
    </DesignShell>
  );
}

function ChoosePiece({ available, onClose, onAdded }: { available: Evidence[]; onClose: () => void; onAdded: () => void }) {
  const [evidenceId, setEvidenceId] = useState(available[0]?.id ?? '');
  const [words, setWords] = useState('');
  const save = useAction(async () => {
    await api.post('/pyp/me/pieces', { evidenceId, narrative: words });
    onAdded();
  });
  return (
    <Modal
      title="Add a piece to your portfolio"
      blurb="Pick your work and say why you chose it."
      onClose={onClose}
      footer={<><Btn kind="secondarySm" onClick={onClose}>Cancel</Btn><Btn kind="primarySm" disabled={!evidenceId || !words.trim() || save.pending} onClick={() => save.run()}>{save.pending ? 'Adding…' : 'Add piece'}</Btn></>}
    >
      <div style={{ display: 'grid', gap: '14px' }}>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Your work
          <select className="input" value={evidenceId} onChange={(e) => setEvidenceId(e.target.value)}>
            {available.map((e) => <option key={e.id} value={e.id}>{e.title} · {kindLabel(e)}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Why you chose it
          <textarea className="input" rows={3} value={words} onChange={(e) => setWords(e.target.value)} maxLength={600} placeholder="I was nervous but I used my numbers." />
        </label>
        {save.error ? <ErrorNote error={save.error} /> : null}
      </div>
    </Modal>
  );
}

/** Route-level switch (§2.4): a PYP student gets the PYP screen, everyone else the usual one. */
export function ByProgramme({ pyp, other }: { pyp: ReactNode; other: ReactNode }) {
  const { user } = useSession();
  return <>{user?.role === 'student' && user.programmes?.[0] === 'pyp' ? pyp : other}</>;
}
